import React, { useState, useEffect, useRef, useCallback } from "react";
import { faFilter, faUndo } from "@fortawesome/free-solid-svg-icons";
import Icon from "../components/UI/icon";
import Button from "../components/UI/button";
import { ref, getDatabase, onValue, get, update } from "firebase/database";
import UsersTable from "../components/usersTable";
import Loader from "../components/UI/loader";
import FilterPopup from "../components/UI/filterPopup";
import { toastrSuccess, toastrError } from "../components/toastr";

const UNDO_DURATION = 30;

const TABS = [
  { key: "waiting", label: "Waiting", color: "text-yellow-700 bg-yellow-50 border-yellow-200" },
  { key: "seated",  label: "Seated",  color: "text-blue-700 bg-blue-50 border-blue-200" },
  { key: "done",    label: "Done",    color: "text-green-700 bg-green-50 border-green-200" },
];

function Dashboard() {
  const [customers, setCustomers] = useState([]);
  const database = getDatabase();
  const customerRef = ref(database, "customers");
  const [loader, setLoader] = useState(false);
  const email = localStorage.getItem("email");
  const ownerId = localStorage.getItem("ownerId");
  const [activeTab, setActiveTab] = useState("waiting");
  const [filteredData, setFilteredData] = useState([]);
  const [showFilter, setFilter] = useState(false);
  const [date, setDate] = useState(new Date());
  const [type, setType] = useState("all");
  const [undoData, setUndoData] = useState(null);
  const [undoLabel, setUndoLabel] = useState("");
  const [undoSeconds, setUndoSeconds] = useState(0);
  const undoTimerRef = useRef(null);
  const undoCountdownRef = useRef(null);

  const clearUndoTimers = useCallback(() => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (undoCountdownRef.current) clearInterval(undoCountdownRef.current);
  }, []);

  useEffect(() => {
    return () => clearUndoTimers();
  }, [clearUndoTimers]);

  const handleStartUndo = useCallback(
    (customer, label) => {
      clearUndoTimers();
      setUndoData(customer);
      setUndoLabel(label);
      setUndoSeconds(UNDO_DURATION);

      undoCountdownRef.current = setInterval(() => {
        setUndoSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(undoCountdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      undoTimerRef.current = setTimeout(() => {
        setUndoData(null);
        setUndoSeconds(0);
      }, UNDO_DURATION * 1000);
    },
    [clearUndoTimers]
  );

  const handleUndo = useCallback(() => {
    if (!undoData) return;
    clearUndoTimers();

    setLoader(true);
    const custRef = ref(database, "customers");
    get(custRef).then((snapshot) => {
      if (snapshot.exists()) {
        const allData = snapshot.val();
        const userKey = Object.keys(allData).find(
          (key) => allData[key].timeStamp === undoData.timeStamp
        );
        if (userKey) {
          const customerRef = ref(database, `customers/${userKey}`);
          update(customerRef, { ...undoData, status: "waiting", tableNumber: null })
            .then(() => {
              setLoader(false);
              toastrSuccess(`${undoData.userName} moved back to queue`);
            })
            .catch(() => {
              setLoader(false);
              toastrError("Error undoing action");
            });
        } else {
          setLoader(false);
        }
      }
    });

    setUndoData(null);
    setUndoSeconds(0);
  }, [undoData, clearUndoTimers, database]);

  useEffect(() => {
    setLoader(true);
    const unsubscribe = onValue(customerRef, (snapshot) => {
      setLoader(false);
      const data = snapshot.val();
      if (data) {
        const projects = Object.values(data).filter(
          (project) => project.email === email || project.ownerId === ownerId
        );
        setCustomers(projects);
      } else {
        setCustomers([]);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (customers) {
      let filtered = customers.filter((i) => {
        const matchesDate =
          new Date(i.timeStamp).toDateString() === new Date(date).toDateString();
        const matchesType = type === "all" || i.category === type;
        let matchesTab;
        if (activeTab === "waiting") matchesTab = i.status === "waiting";
        else if (activeTab === "seated") matchesTab = i.status === "seated";
        else matchesTab = i.status === "done" || i.status === "canceled";
        return matchesDate && matchesType && matchesTab;
      });

      // Sort waiting: priority first, then by tokenNumber
      if (activeTab === "waiting") {
        filtered = filtered.sort((a, b) => {
          if (a.isPriority && !b.isPriority) return -1;
          if (!a.isPriority && b.isPriority) return 1;
          return (a.tokenNumber || 0) - (b.tokenNumber || 0);
        });
      }
      setFilteredData(filtered);
    }
  }, [activeTab, customers, date, type]);

  const getTabCount = (tabKey) => {
    const today = new Date().toDateString();
    if (tabKey === "waiting") return customers.filter((c) => c.status === "waiting" && new Date(c.timeStamp).toDateString() === today).length;
    if (tabKey === "seated")  return customers.filter((c) => c.status === "seated"  && new Date(c.timeStamp).toDateString() === today).length;
    return customers.filter((c) => (c.status === "done" || c.status === "canceled") && new Date(c.timeStamp).toDateString() === today).length;
  };

  const onClose = () => setFilter(false);

  const todayStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  const undoRingColor = undoLabel === "seated" ? "#3b82f6" : undoLabel === "done" ? "#22c55e" : "#ef4444";

  return (
    <div className="max-w-md mx-auto">
      <div className="pl-5 pr-14 pt-6 pb-2 flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{todayStr}</p>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Dashboard</h1>
        </div>
        <Button
          onClick={() => setFilter(!showFilter)}
          className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white mt-1"
        >
          <Icon icon={faFilter} className="text-sm" />
        </Button>
      </div>

      {/* Summary cards */}
      <div className="flex items-center gap-2 mx-5 mt-3 mb-1">
        {[
          { label: "Waiting",  key: "waiting",  cls: "bg-yellow-50 text-yellow-700" },
          { label: "Seated",   key: "seated",   cls: "bg-blue-50 text-blue-700" },
          { label: "Done",     key: "done",     cls: "bg-green-50 text-green-700" },
        ].map(({ label, key, cls }) => (
          <div key={key} className={`flex-1 rounded-xl p-2.5 text-center ${cls}`}>
            <p className="text-lg font-bold">{getTabCount(key)}</p>
            <p className="text-xs">{label}</p>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mx-5 mt-3 mb-3 bg-gray-100 rounded-xl p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {getTabCount(tab.key) > 0 && (
              <span className={`ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
              }`}>
                {getTabCount(tab.key)}
              </span>
            )}
          </button>
        ))}
      </div>

      {filteredData && filteredData.length > 0 ? (
        <UsersTable
          customers={filteredData}
          setLoader={setLoader}
          activeTab={activeTab}
          onStartUndo={handleStartUndo}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            {activeTab === "waiting" ? "No one waiting" : activeTab === "seated" ? "No one seated" : "No completed entries"}
          </h2>
          <p className="text-sm text-gray-500 text-center">
            {activeTab === "waiting" && date.toDateString() === new Date().toDateString()
              ? "Tap the + button below to add your first customer"
              : date.toDateString() !== new Date().toDateString()
              ? `No entries for ${date.toLocaleDateString()}`
              : ""}
          </p>
        </div>
      )}

      {undoData && (
        <div className="fixed bottom-20 left-4 right-4 z-50 flex justify-center">
          <div className="bg-gray-900 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-sm w-full">
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {undoData.userName} — {undoLabel}
              </p>
              <p className="text-gray-400 text-xs">{undoSeconds}s to undo</p>
            </div>
            <div className="w-8 h-8 relative flex items-center justify-center">
              <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="14" fill="none" stroke="#374151" strokeWidth="2" />
                <circle
                  cx="16" cy="16" r="14" fill="none"
                  stroke={undoRingColor}
                  strokeWidth="2"
                  strokeDasharray={2 * Math.PI * 14}
                  strokeDashoffset={2 * Math.PI * 14 * (1 - undoSeconds / UNDO_DURATION)}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1s linear" }}
                />
              </svg>
              <span className="absolute text-white text-xs font-bold">{undoSeconds}</span>
            </div>
            <button
              onClick={handleUndo}
              className="flex items-center gap-1.5 bg-white text-gray-900 text-sm font-semibold px-4 py-2 rounded-xl hover:bg-gray-100"
            >
              <Icon icon={faUndo} className="text-xs" />
              Undo
            </button>
          </div>
        </div>
      )}

      {loader && <Loader loading={loader} />}
      {showFilter && (
        <FilterPopup
          open={showFilter}
          onClose={onClose}
          setDate={setDate}
          date={date}
          type={type}
          setType={setType}
        />
      )}
    </div>
  );
}

export default Dashboard;
